import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface SubmenuGroupItemListProps<OptionType extends CascaderOption>
  extends InternalSubmenuGroupItemListProps {
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

interface InternalSubmenuGroupItemListProps extends BoxProps {
  submenuGroupItemsRef?: React.Ref<HTMLDivElement>;
}

export const InternalSubmenuGroupItemList = <
  OptionType extends CascaderOption,
>({
  submenuGroupItemsRef,
  children,
  ...rest
}: InternalSubmenuGroupItemListProps) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSubmenuGroupItemList = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SUBMENU_GROUP_ITEM_LIST),
    [getComponent],
  );

  return (
    <CustomSubmenuGroupItemList
      submenuGroupItemsRef={submenuGroupItemsRef}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.submenuGroupItemList}
      {...rest}
      className="cascader-submenu-group-item-list"
    >
      {children}
    </CustomSubmenuGroupItemList>
  );
};

export const SubmenuGroupItemList = <OptionType extends CascaderOption>({
  submenuGroupItemsRef,
  children,
  getCommonProps: _,
  ...rest
}: SubmenuGroupItemListProps<OptionType>) => {
  return (
    <Box as="ul" ref={submenuGroupItemsRef} {...rest}>
      {children}
    </Box>
  );
};
